import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import styles from './styles.module.scss';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import { StudySiteDto } from '@interfaces/central-monitoring';
import {
    SiteSelectorItem,
    StudySiteSelectorListItem,
} from '@pages-clean/central-monitoring/components/site-selector/study-selector-item';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { setSessionStorageSiteCode } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { useLocalState } from '@hooks/index';
import { notEmpty } from '@utils/utility-helpers';
import { useGetDatasetSites } from '@apis/bff-api/central-monitoring';

const SiteSelector = () => {
    const anchor = useRef<HTMLDivElement | null>(null);

    // Getting this info to get the member which contains the details of a default and fav sites
    const userContext = useContext(UserContext);
    const userId = userContext.user?.id;
    const { state } = useLocalState();
    const member = state?.selectedStudy?.members?.find((studyMember) => studyMember?.userId === userId);

    const { current, setSiteId, setSiteName } = useContext(StudySiteHubContext);
    const { data: sites, isLoading: isLoadingSites } = useGetDatasetSites(current.studyRef, current.dataset!.id);

    const orderedSites = useMemo(() => {
        if (sites) {
            const sitesWithFavourites: SiteSelectorItem[] = sites.filter(notEmpty).map((site: StudySiteDto) => {
                return {
                    id: site.id,
                    siteName: site.siteName,
                    code: site.code,
                    favourite: member?.favouriteSiteIds?.includes(site.siteRef) ?? false,
                    _isDefault: member?.defaultSiteId === site.siteRef,
                };
            });

            return orderBy(sitesWithFavourites, [
                { field: '_isDefault', dir: 'desc' },
                { field: 'favourite', dir: 'desc' },
                { field: 'siteName', dir: 'asc' },
            ]);
        }

        return sites;
    }, [sites]);

    useEffect(() => {
        if (!orderedSites) return;

        const defaultSiteId =
            current.siteCode && orderedSites.some((s) => s.code === current.siteCode)
                ? orderedSites.find((s) => s.code === current.siteCode)!.id
                : member?.defaultSiteId && orderedSites.some((s) => s.code === member?.defaultSiteId)
                  ? orderedSites.find((s) => s.code === member?.defaultSiteId)!.id
                  : orderedSites[0]?.id;

        const defaultSiteName = orderedSites.find((s) => s.id === defaultSiteId)?.siteName;
        setSiteId!(orderedSites.length > 0 ? defaultSiteId : undefined);
        setSiteName!(orderedSites.length > 0 ? defaultSiteName : undefined);
    }, [orderedSites]);

    return (
        <div ref={anchor} className="k-d-flex k-align-items-center">
            <BasicDropDown
                data-testid="site-selector"
                data={orderedSites ?? []}
                dataItemKey="id"
                textField="siteName"
                value={orderedSites ? orderedSites.find((s) => s.id === current.siteId) : undefined}
                onChange={(e) => {
                    const site = e.value as StudySiteDto;
                    setSiteId!(site.id);
                    setSiteName!(site.siteName);
                    setSessionStorageSiteCode(site.code);
                }}
                itemRender={(li, itemProps) => (
                    <StudySiteSelectorListItem li={li} dataItem={itemProps.dataItem as SiteSelectorItem} />
                )}
                loading={isLoadingSites}
                type="card-header"
                className={styles.siteSelector}
                filterable
                customClassName="basic-dropdown-styling"
                cardHeader
            />
        </div>
    );
};

export default SiteSelector;
